import { todayPT } from "@homebound/activesupport";
import { useCallback, useState } from "react";
import { Temporal } from "temporal-polyfill";

// Hardcoding that weeks start on a Monday as a standard, can be made a prop if this needs to change
const WEEK_STARTS_ON = 1;
export const DAYS_IN_WEEK = 7;

type RangeFilterOps = { weekCount: number; initialStartDate?: Temporal.PlainDate };

export type WeekInterval = { start: Temporal.PlainDate; end: Temporal.PlainDate; index: number; weekID: number };

export type RangeFilterReturnTypes = {
  dateRange: { start: Temporal.PlainDate; end: Temporal.PlainDate };
  nextRangeGroup: () => void;
  previousRangeGroup: () => void;
  setStartDate: (newStartDate: Temporal.PlainDate) => void;
  includedWeekIntervals: WeekInterval[];
  weekCount: number;
};

/** Custom filter for reports that allows for chunking dates into week groupings */
export function useWeekRangeFilter({ initialStartDate, weekCount }: RangeFilterOps): RangeFilterReturnTypes {
  if (initialStartDate) validateStartDate(initialStartDate);

  const startOfCurrentWeek = startOfWeek(todayPT());

  const [dateRange, setDateRange] = useState(
    createDateRange({ start: initialStartDate ?? startOfCurrentWeek, weekCount }),
  );

  const nextRangeGroup = useCallback(() => {
    const newStart = dateRange.end.add({ days: 1 });
    setDateRange(createDateRange({ start: newStart, weekCount }));
  }, [dateRange.end, weekCount]);

  const previousRangeGroup = useCallback(() => {
    const newStart = dateRange.start.subtract({ days: weekCount * DAYS_IN_WEEK });

    setDateRange(createDateRange({ start: newStart, weekCount }));
  }, [dateRange.start, weekCount]);

  const setStartDate = useCallback(
    (newStartDate: Temporal.PlainDate) => {
      validateStartDate(newStartDate);
      setDateRange(createDateRange({ start: newStartDate, weekCount }));
    },
    [weekCount],
  );

  return {
    dateRange,
    nextRangeGroup,
    previousRangeGroup,
    setStartDate,
    includedWeekIntervals: getIncludedWeekIntervals(dateRange),
    weekCount,
  };
}

export function defaultWeekRangeForToday(today: Temporal.PlainDate, weekCount: number) {
  return createDateRange({ start: startOfWeek(today), weekCount });
}

/** End date is not inclusive of the following Monday (ends Sunday) */
function createDateRange({ start, weekCount }: { start: Temporal.PlainDate; weekCount: number }) {
  const end = start.add({ days: weekCount * DAYS_IN_WEEK - 1 });
  return { start, end };
}

function validateStartDate(startDate: Temporal.PlainDate) {
  if (startDate.dayOfWeek !== WEEK_STARTS_ON) {
    throw new Error("Valid ranges must begin on a Monday");
  }
}

/** Used downstream to further group data into the individual weeks contained in the range.
 * * Use the `weekID` unix timestamp as a stable reference to a given week */
function getIncludedWeekIntervals({ start, end }: { start: Temporal.PlainDate; end: Temporal.PlainDate }) {
  const includedWeekCount = (start.until(end, { largestUnit: "day" }).days + 1) / DAYS_IN_WEEK;

  return Array.from({ length: includedWeekCount }, (_, index) => {
    const weekStart = start.add({ weeks: index });

    return { start: weekStart, end: weekStart.add({ days: DAYS_IN_WEEK - 1 }), index, weekID: getWeekId(weekStart) };
  });
}

function startOfWeek(date: Temporal.PlainDate) {
  return date.subtract({ days: date.dayOfWeek - WEEK_STARTS_ON });
}

function getWeekId(date: Temporal.PlainDate) {
  return Temporal.Instant.from(`${date}T00:00:00.000Z`).epochMilliseconds / 1000;
}
